import { useHistory } from "react-router-dom";
import { useState, useEffect, useContext, useRef } from "react";
import { SystemLanguagesContext } from "../../../contexts/SystemLanguagesContext";
import { APIContext } from "../../../contexts/APIContext";
import { UserContext } from "../../../contexts/UserContext";
import { saveSharedUserInfo } from "../../../utils/cookies/userInfo";
import { CEFR_LEVELS } from "../../../assorted/cefrLevels";
import { getUserCefrLevel } from "../../../utils/misc/userCefrLevel";
import strings from "../../../i18n/definitions";
import LocalStorage from "../../../assorted/LocalStorage";
import LoadingAnimation from "../../../components/LoadingAnimation";
import LanguageSelector from "../../../components/LanguageSelector";
import Button from "../../_pages_shared/Button.sc";
import ButtonContainer from "../../_pages_shared/ButtonContainer.sc";
import Form from "../../_pages_shared/Form.sc";
import FormSection from "../../_pages_shared/FormSection.sc";
import CardPage from "../../_pages_shared/CardPage";
import Main from "../../_pages_shared/Main.sc";
import FullWidthErrorMsg from "../../../components/FullWidthErrorMsg.sc";
import SettingsPageHeader from "../SharedComponents/SettingsPageHeader";
import Selector from "../../../components/Selector";
import { setTitle } from "../../../assorted/setTitle";
import useFormField from "../../../hooks/useFormField";
import { NonEmptyValidator, Validator } from "../../../utils/ValidatorRule/Validator";
import useShadowRef from "../../../hooks/useShadowRef";
import { scrollToTop } from "../../../utils/misc/scrollToTop";
import validateRules from "../../../assorted/validateRules";

export default function LanguageSettings() {
  const api = useContext(APIContext);
  const { sortedSystemLanguages } = useContext(SystemLanguagesContext);
  const { userDetails, setUserDetails, session } = useContext(UserContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [CEFR, setCEFR] = useState(null);

  const [
    learnedLanguage,
    setLearnedLanguage,
    validateLearnedLanguage,
    isLearnedLanguageValid,
    learnedLanguageErrorMsg,
  ] = useFormField("", NonEmptyValidator("Please select a language."));

  const learnedLanguageRef = useShadowRef(learnedLanguage);
  const [nativeLanguage, setNativeLanguage, validateNativeLanguage, isNativeLanguageValid, nativeLanguageMsg] =
    useFormField("en", [
      NonEmptyValidator("Please select a language."),
      new Validator((v) => {
        return v !== learnedLanguageRef.current;
      }, "Your Translation language needs to be different than your learned language."),
    ]);
  const history = useHistory();
  const isPageMounted = useRef(true);

  function setCEFRLevelFromUserContext(data) {
    setCEFR(getUserCefrLevel(data, data.learned_language));
  }

  useEffect(() => {
    setTitle(strings.languageSettings);
  }, []);

  useEffect(() => {
    isPageMounted.current = true;

    if (isPageMounted.current) {
      setCEFRLevelFromUserContext(userDetails);
      setLearnedLanguage(userDetails.learned_language);
      setNativeLanguage(userDetails.native_language);
    }

    return () => {
      isPageMounted.current = false;
    };
    // eslint-disable-next-line
  }, [session, api]);

  function handleSave(e) {
    e.preventDefault();
    if (!validateRules([validateLearnedLanguage, validateNativeLanguage])) scrollToTop();
    else {
      const newUserDetails = {
        ...userDetails,
        learned_language: learnedLanguage,
        native_language: nativeLanguage,
        [learnedLanguage + "_cefr_level"]: CEFR,
      };

      const newUserDetailsForAPI = {
        ...newUserDetails,
        cefr_level: CEFR,
      };

      api.saveUserDetails(newUserDetailsForAPI, setErrorMessage, () => {
        setUserDetails(newUserDetails);
        LocalStorage.setUserInfo(newUserDetails);
        saveSharedUserInfo(newUserDetails);
        history.goBack();
      });
    }
  }

  if (!userDetails || !sortedSystemLanguages) {
    return <LoadingAnimation />;
  }

  return (
    <CardPage layoutVariant={"card-under-menu"} isTransparent reducedPadding>
      <SettingsPageHeader title={strings.languageSettings} />
      <Main>
        <Form>
          <FormSection>
            {errorMessage && <FullWidthErrorMsg>{errorMessage}</FullWidthErrorMsg>}
            <LanguageSelector
              id={"practiced-language-selector"}
              label={strings.learnedLanguage}
              languages={sortedSystemLanguages.learnable_languages}
              selected={learnedLanguage}
              onChange={(e) => {
                setLearnedLanguage(e.target.value);
              }}
              isError={!isLearnedLanguageValid}
              errorMessage={learnedLanguageErrorMsg}
            />

            <Selector
              id={"cefr-levels-selector"}
              options={CEFR_LEVELS}
              optionLabel={(e) => e.label}
              optionValue={(e) => e.value}
              onChange={(e) => {
                setCEFR(parseInt(e.target.value));
              }}
              selectedValue={CEFR}
            />
            {CEFR_LEVELS.find((l) => l.value === String(CEFR))?.description && (
              <p style={{ fontSize: "0.85em", color: "#888", margin: "-4px 0 8px" }}>
                {CEFR_LEVELS.find((l) => l.value === String(CEFR)).description}
              </p>
            )}
          </FormSection>

          <FormSection>
            <LanguageSelector
              id={"translation-language-selector"}
              label={strings.baseLanguage}
              languages={sortedSystemLanguages.native_languages}
              selected={nativeLanguage}
              isError={!isNativeLanguageValid}
              errorMessage={nativeLanguageMsg}
              onChange={(e) => {
                setNativeLanguage(e.target.value);
              }}
            />
          </FormSection>
          <ButtonContainer className={"adaptive-alignment-horizontal"}>
            <Button type={"submit"} onClick={handleSave}>
              {strings.save}
            </Button>
          </ButtonContainer>
        </Form>
      </Main>
    </CardPage>
  );
}
